import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { RouteComponentProps } from "react-router-dom";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import { useTenantService, SubscriptionsReport, TenantLifecycleStatus, SiteLifecycleStatus } from "../tenants/tenantService";
import { useSiteService } from "../sites/siteService";
import { subscriptionsTenantsStatusFilterAtom, subscriptionsSiteStatusFilterAtom } from "../shared/filterState";

export default function SubscriptionsReportPage(props: RouteComponentProps) {
  const [report, setReport] = useState<SubscriptionsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const tenantService = useTenantService();
  const siteService = useSiteService();

  // Filter state
  const [selectedTenantStatuses, setSelectedTenantStatuses] = useRecoilState(subscriptionsTenantsStatusFilterAtom);
  const [selectedSiteStatuses, setSelectedSiteStatuses] = useRecoilState(subscriptionsSiteStatusFilterAtom);

  // Tenant editing state
  const [editingTenantId, setEditingTenantId] = useState<string | null>(null);
  const [editTenantSitePrice, setEditTenantSitePrice] = useState<string>("");
  const [editTenantEntityPrice, setEditTenantEntityPrice] = useState<string>("");

  // Site editing state
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [editSitePriceSite, setEditSitePriceSite] = useState<string>("");
  const [editSitePriceEntity, setEditSitePriceEntity] = useState<string>("");

  const loadReport = async () => {
    try {
      setLoading(true);
      const data = await tenantService.getSubscriptionsReport(
        selectedTenantStatuses.length > 0 ? selectedTenantStatuses : undefined,
        selectedSiteStatuses.length > 0 ? selectedSiteStatuses : undefined
      );
      setReport(data);
    } catch (err: any) {
      setError(err.message || "Failed to load subscriptions report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [selectedTenantStatuses, selectedSiteStatuses]);

  const handleTenantStatusChange = (event: SelectChangeEvent<TenantLifecycleStatus[]>) => {
    const value = event.target.value;
    setSelectedTenantStatuses(typeof value === 'string' ? value.split(',') as TenantLifecycleStatus[] : value);
  };

  const handleSiteStatusChange = (event: SelectChangeEvent<SiteLifecycleStatus[]>) => {
    const value = event.target.value;
    setSelectedSiteStatuses(typeof value === 'string' ? value.split(',') as SiteLifecycleStatus[] : value);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("nb-NO", {
      style: "currency",
      currency: "NOK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const startEditTenant = (tenantId: string, defaultSitePrice: number, defaultEntityPrice: number) => {
    setEditingTenantId(tenantId);
    setEditTenantSitePrice(String(defaultSitePrice));
    setEditTenantEntityPrice(String(defaultEntityPrice));
  };

  const cancelEditTenant = () => {
    setEditingTenantId(null);
  };

  const saveTenantPrices = async (tenantId: string) => {
    setSaving(true);
    try {
      await tenantService.postCommand(tenantId, {
        type: "UpdateSubscriptionPricesCommand",
        site: Number(editTenantSitePrice),
        entity: Number(editTenantEntityPrice),
      });
      setEditingTenantId(null);
      await loadReport();
    } catch (err: any) {
      setError(err.message || "Failed to update tenant prices");
    } finally {
      setSaving(false);
    }
  };

  const startEditSite = (siteId: string, currentPrices: { site: number; entity: number } | null) => {
    setEditingSiteId(siteId);
    setEditSitePriceSite(currentPrices != null ? String(currentPrices.site) : "");
    setEditSitePriceEntity(currentPrices != null ? String(currentPrices.entity) : "");
  };

  const cancelEditSite = () => {
    setEditingSiteId(null);
  };

  const saveSiteSubscriptionPrices = async (siteId: string) => {
    setSaving(true);
    try {
      const sitePrice = editSitePriceSite === "" ? null : Number(editSitePriceSite);
      const entityPrice = editSitePriceEntity === "" ? null : Number(editSitePriceEntity);
      await siteService.postCommand(siteId, {
        type: "UpdateSiteSubscriptionPricesCommand",
        site: sitePrice,
        entity: entityPrice,
      });
      setEditingSiteId(null);
      await loadReport();
    } catch (err: any) {
      setError(err.message || "Failed to update site subscription prices");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!report) {
    return null;
  }

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Subscriptions Report
        </Typography>

        {/* Filter controls */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Filtrer på lifecycle status
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Tenant Status</InputLabel>
              <Select
                multiple
                value={selectedTenantStatuses}
                onChange={handleTenantStatusChange}
                input={<OutlinedInput label="Tenant Status" />}
                renderValue={(selected) => selected.join(', ')}
              >
                {Object.values(TenantLifecycleStatus).map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Site Status</InputLabel>
              <Select
                multiple
                value={selectedSiteStatuses}
                onChange={handleSiteStatusChange}
                input={<OutlinedInput label="Site Status" />}
                renderValue={(selected) => selected.join(', ')}
              >
                {Object.values(SiteLifecycleStatus).map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6">
            Total ARR: <strong>{formatCurrency(report.sumTotal)}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {report.tenantsSubscriptions.length} tenants
          </Typography>
        </Paper>
      </Box>

      {report.tenantsSubscriptions.map((tenant, index) => (
        <Accordion key={index} defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" width="100%" justifyContent="space-between" pr={2}>
              <Typography variant="h6">{tenant.tenantName}</Typography>
              <Box display="flex" gap={2}>
                <Chip
                  label={`${tenant.sites.length} sites`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={formatCurrency(tenant.totalSubscription)}
                  size="small"
                  color="success"
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Tenant default prices */}
            <Box sx={{ mb: 2, p: 1, bgcolor: "grey.50", borderRadius: 1 }}>
              {editingTenantId === tenant.tenantId ? (
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <TextField
                    label="Standard sitepris"
                    size="small"
                    value={editTenantSitePrice}
                    onChange={(e) => setEditTenantSitePrice(e.target.value)}
                  />
                  <TextField
                    label="Standard entitypris"
                    size="small"
                    value={editTenantEntityPrice}
                    onChange={(e) => setEditTenantEntityPrice(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    disabled={saving}
                    onClick={() => saveTenantPrices(tenant.tenantId)}
                  >
                    {saving ? <CircularProgress size={20} /> : "Lagre"}
                  </Button>
                  <Button size="small" onClick={cancelEditTenant} disabled={saving}>
                    Avbryt
                  </Button>
                </Box>
              ) : (
                <Box display="flex" gap={3} alignItems="center">
                  <Typography variant="body2">
                    Standard sitepris: <strong>{formatCurrency(tenant.defaultSitePrice)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Standard entitypris: <strong>{formatCurrency(tenant.defaultEntityPrice)}</strong>
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => startEditTenant(tenant.tenantId, tenant.defaultSitePrice, tenant.defaultEntityPrice)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Site Name</TableCell>
                    <TableCell align="right">Valves</TableCell>
                    <TableCell align="right">Site Override (Sitepris / Entitypris)</TableCell>
                    <TableCell align="right">Site ARR</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenant.sites.map((site, siteIndex) => (
                    <TableRow key={siteIndex}>
                      <TableCell>{site.siteName}</TableCell>
                      <TableCell align="right">{site.numberOfEntities}</TableCell>
                      <TableCell align="right">
                        {editingSiteId === site.siteId ? (
                          <Box display="flex" gap={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
                            <TextField
                              label="Sitepris"
                              size="small"
                              value={editSitePriceSite}
                              onChange={(e) => setEditSitePriceSite(e.target.value)}
                              sx={{ width: 100 }}
                              placeholder="Standard"
                            />
                            <TextField
                              label="Entitypris"
                              size="small"
                              value={editSitePriceEntity}
                              onChange={(e) => setEditSitePriceEntity(e.target.value)}
                              sx={{ width: 100 }}
                              placeholder="Standard"
                            />
                            <Button
                              variant="contained"
                              size="small"
                              disabled={saving}
                              onClick={() => saveSiteSubscriptionPrices(site.siteId)}
                            >
                              {saving ? <CircularProgress size={16} /> : "Lagre"}
                            </Button>
                            <Button size="small" onClick={cancelEditSite} disabled={saving}>
                              Avbryt
                            </Button>
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                            <span>
                              {site.siteSubscriptionPrices != null
                                ? `${formatCurrency(site.siteSubscriptionPrices.site)} / ${formatCurrency(site.siteSubscriptionPrices.entity)}`
                                : "Standard"}
                            </span>
                            <IconButton
                              size="small"
                              onClick={() => startEditSite(site.siteId, site.siteSubscriptionPrices)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(site.subscription)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3}>
                      <strong>Tenant Total</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(tenant.totalSubscription)}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}

      <Paper sx={{ p: 2, mt: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Summary</strong>
                </TableCell>
                <TableCell align="right">Sites</TableCell>
                <TableCell align="right">Total Valves</TableCell>
                <TableCell align="right">Total ARR</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <strong>Grand Total</strong>
                </TableCell>
                <TableCell align="right">
                  {report.tenantsSubscriptions.reduce((sum, t) => sum + t.sites.length, 0)}
                </TableCell>
                <TableCell align="right">
                  {report.tenantsSubscriptions.reduce(
                    (sum, t) => sum + t.sites.reduce((siteSum, s) => siteSum + s.numberOfEntities, 0),
                    0
                  )}
                </TableCell>
                <TableCell align="right">
                  <strong>{formatCurrency(report.sumTotal)}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
